import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { apiClient } from "@/lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Salut 👋 Je suis ton coach Pousse. Une question sur le sport, la nutrition ou l'app ?" },
  ]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await apiClient.post("/chat", { message: text, conversation_id: convId });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, je n'ai pas pu répondre. Réessaie." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 z-50 h-14 w-14 rounded-full bg-lime-400 text-[#0A0F0D] flex items-center justify-center shadow-[0_0_24px_rgba(200,255,0,0.4)] active:scale-95 transition-transform sm:bottom-6"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="fixed inset-x-0 bottom-0 z-50 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-96">
          <div className="glass border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col h-[70vh] sm:h-[520px]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0f1512]">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-lime-400" />
                <span className="font-display font-bold">Coach Pousse</span>
              </div>
              <button data-testid="chatbot-close-button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} data-testid="chatbot-message-bubble"
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-lime-400 text-[#0A0F0D] font-medium"
                      : "bg-white/5 text-gray-200 border border-white/10"
                  }`}>
                  {m.content}
                </div>
              ))}
              {loading && (
                <div className="bg-white/5 border border-white/10 rounded-2xl px-3.5 py-2.5 w-16 flex gap-1">
                  <span className="h-2 w-2 rounded-full bg-lime-400/70 animate-bounce" />
                  <span className="h-2 w-2 rounded-full bg-lime-400/70 animate-bounce [animation-delay:0.15s]" />
                  <span className="h-2 w-2 rounded-full bg-lime-400/70 animate-bounce [animation-delay:0.3s]" />
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                data-testid="chatbot-input-field"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écris ton message…"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-lime-400/50"
              />
              <button data-testid="chatbot-send-button" onClick={send}
                className="h-10 w-10 rounded-full bg-lime-400 text-[#0A0F0D] flex items-center justify-center active:scale-95 transition-transform">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
